import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash2, Pencil, FolderOpen, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatPrice, FURNITURE_TYPES } from '@/lib/furnitureData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = Object.fromEntries(FURNITURE_TYPES.map(ft => [ft.id, ft.nameRu]));

export default function SavedPage() {
  const navigate = useNavigate();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadConfigs = () => {
    setLoading(true);
    axios.get(`${API}/configurations`).then(r => {
      setConfigs(r.data);
    }).catch(() => {
      toast.error('Ошибка загрузки проектов');
    }).finally(() => setLoading(false));
  };

  useEffect(() => { loadConfigs(); }, []);

  const handleDelete = async (id, name) => {
    try {
      await axios.delete(`${API}/configurations/${id}`);
      setConfigs(prev => prev.filter(c => c.id !== id));
      toast.success(`Проект "${name}" удалён`);
    } catch {
      toast.error('Ошибка удаления');
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-10" data-testid="saved-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1
            className="text-2xl md:text-3xl font-bold tracking-tight text-[#1C1C1C]"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            Мои проекты
          </h1>
          <p className="text-sm text-[#5C5C5C] mt-1">Сохранённые конфигурации мебели</p>
        </div>
        <Button
          onClick={() => navigate('/builder')}
          className="bg-[#C25934] hover:bg-[#A64A2B] text-white rounded-sm h-9 text-sm"
          data-testid="new-project-btn"
        >
          <Plus className="w-4 h-4 mr-1.5" />
          Новый проект
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-6 h-6 border-2 border-[#C25934] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : configs.length === 0 ? (
        <div className="text-center py-20" data-testid="no-projects">
          <FolderOpen className="w-12 h-12 text-[#E0DDD8] mx-auto mb-4" />
          <p className="text-sm text-[#5C5C5C]">Нет сохранённых проектов</p>
          <Button
            onClick={() => navigate('/builder')}
            variant="outline"
            className="mt-4 rounded-sm border-[#C25934] text-[#C25934] hover:bg-[#C25934]/5"
            data-testid="create-first-project-btn"
          >
            Создать первый проект
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {configs.map((cfg, i) => (
            <div
              key={cfg.id}
              className="bg-white rounded-sm border border-[#E0DDD8] p-5 template-card animate-fade-in-up"
              style={{ animationDelay: `${i * 60}ms` }}
              data-testid={`config-card-${cfg.id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-[#1C1C1C] text-sm" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                    {cfg.name}
                  </h3>
                  <Badge variant="secondary" className="bg-[#E8E5E1] text-[#5C5C5C] text-[10px] rounded-sm mt-1">
                    {TYPE_LABELS[cfg.furniture_type] || cfg.furniture_type}
                  </Badge>
                </div>
                <span className="mono-text text-sm font-semibold text-[#C25934]">
                  {formatPrice(cfg.total_price)}
                </span>
              </div>

              <div className="flex items-center gap-3 text-[10px] mono-text text-[#5C5C5C] mb-4">
                <span>{cfg.modules?.length || 0} модулей</span>
                <span>{new Date(cfg.created_at).toLocaleDateString('ru-RU')}</span>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  onClick={() => navigate(`/builder?config=${cfg.id}`)}
                  className="flex-1 bg-[#C25934] hover:bg-[#A64A2B] text-white rounded-sm text-xs h-8"
                  data-testid={`open-config-${cfg.id}`}
                >
                  <Pencil className="w-3 h-3 mr-1" />
                  Открыть
                </Button>
                <Button
                  size="sm" variant="outline"
                  onClick={() => handleDelete(cfg.id, cfg.name)}
                  className="rounded-sm text-xs h-8 border-[#E0DDD8] text-red-500 hover:bg-red-50 hover:text-red-600"
                  data-testid={`delete-config-${cfg.id}`}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
